import React, { Component } from "react";
import {
  StyleSheet,
  Modal,
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Platform,
} from "react-native";
import { connect } from "react-redux";
import { MaterialIcons, MaterialCommunityIcons } from "@expo/vector-icons";
import colors from "../constants/colors";
import { searchItems, searchInventoryTypes, searchPackagingMaterials } from "../actions/records";
import { getIngredientLotsByName } from "../actions/submissions";
import { sortSearch, validateDecimals } from "../utils";
import { isMobileWeb } from "../utils/mobileWeb";
import { unwrapLocationValue } from "../utils/inventoryTransferLocationTree";
import InventoryTransferLotsPanel, {
  buildInventoryTransferSaveRows,
  findLotMissingToLocation,
  getTransferLocationPathLabel,
  locationsRoughlyMatch,
  resolveTransferLocation,
} from "./InventoryTransferLotsPanel";

const TYPE_TABS = [
  { key: "finishedGoods", label: "Finished Goods" },
  { key: "rawMaterials", label: "Raw Materials" },
  { key: "packagingMaterials", label: "Packaging Materials" },
  { key: "workInProgress", label: "WIP'S" },
];

const TYPE_API = {
  finishedGoods: "fg",
  rawMaterials: "ingredient",
  packagingMaterials: "packagingMaterial",
  workInProgress: "wip",
};

const INVENTORY_TYPE_API = {
  finishedGoods: "Finished goods",
  workInProgress: "Work in progress",
};

function roundTransferAmount(value) {
  const n = parseFloat(value);
  if (isNaN(n)) return 0;
  if (n % 1 !== 0 && !validateDecimals(String(n))) {
    return Number(n.toFixed(3));
  }
  return n;
}

/**
 * Item picker for Inventory Transfer.
 * Selecting items adds lot rows; Transfer details is per lot (shared panel).
 * Save writes one submission row per lot with its own To Location.
 */
class InventoryTransferItemModal extends Component {
  constructor(props) {
    super(props);
    const completed = !!props.complete_date || !!props.readOnly;
    this.state = {
      itemSearch: "",
      toLocationSearch: "",
      selectedType: this.getInitialSelectedType(props),
      options: completed ? this.getCompletedOptions(props) : [],
      loading: false,
      page: 1,
      totalPages: 1,
      selectedLots: [],
      expandedItems: {},
      selectedLotKey: null,
      selectedItemName: null,
      showToLocationDropdown: false,
      hasUserChanges: false,
      allowSeedInitial: true,
      infoMessage: "",
      toLocationError: "",
    };
    this._fetchId = 0;
    this._searchInterval = null;
  }

  isCompleted() {
    return this.isReadOnly();
  }

  isReadOnly() {
    return !!this.props.readOnly;
  }

  getSelectedItem(props = this.props) {
    const selected = props.selectedItem;
    if (!selected) return null;
    if (typeof selected === "string") {
      const name = selected.trim();
      return name ? { item: name, itemName: name } : null;
    }
    if (typeof selected === "object") {
      const name = selected.itemName || selected.item || selected.namePackaging || selected.name || "";
      if (!name && !selected._id) return null;
      return {
        ...selected,
        item: name || selected.item || "",
        itemName: name || selected.itemName || "",
      };
    }
    return null;
  }

  getCompletedOptions(props = this.props) {
    const selected = this.getSelectedItem(props);
    return selected ? [selected] : [];
  }

  getInitialSelectedType(props = this.props) {
    const selected = this.getSelectedItem(props);
    const type =
      selected?.inventoryCategory ||
      selected?.inventoryType ||
      selected?.type ||
      "";
    if (TYPE_TABS.some((t) => t.key === type)) return type;
    const normalized = String(type).trim().toLowerCase();
    if (
      type === "fg" ||
      normalized === "finished goods" ||
      normalized === "finishedgoods"
    ) {
      return "finishedGoods";
    }
    if (
      type === "ingredient" ||
      normalized === "raw materials" ||
      normalized === "rawmaterials"
    ) {
      return "rawMaterials";
    }
    if (
      type === "packagingMaterial" ||
      normalized === "packaging materials" ||
      normalized === "packagingmaterials"
    ) {
      return "packagingMaterials";
    }
    if (
      type === "wip" ||
      normalized === "work in progress" ||
      normalized === "workinprogress" ||
      normalized === "wip's" ||
      normalized === "wips"
    ) {
      return "workInProgress";
    }
    return "finishedGoods";
  }

  componentDidMount() {
    this.seedInitialSelection();
    if (this.isCompleted()) {
      this.setState({ options: this.getCompletedOptions(), loading: false });
      return;
    }
    this.fetchItems("", 1);
  }

  componentWillUnmount() {
    if (this._searchInterval) clearInterval(this._searchInterval);
  }

  scheduleFetch(query) {
    if (this.isCompleted()) return;
    if (this._searchInterval) clearInterval(this._searchInterval);
    this._searchInterval = setInterval(() => {
      if (this._searchInterval) clearInterval(this._searchInterval);
      this._searchInterval = null;
      this.fetchItems(query, 1);
    }, 350);
  }

  fetchItems(query, page = 1) {
    if (this.isCompleted()) {
      this.setState({ options: this.getCompletedOptions(), loading: false, page: 1, totalPages: 1 });
      return;
    }
    const { selectedType } = this.state;
    const fetchId = ++this._fetchId;
    const q = (query || "").trim();
    const pageNum = Math.max(1, page || 1);
    const pageSize = 10;
    this.setState({ loading: true });

    const done = (list, res) => {
      if (fetchId !== this._fetchId) return;
      const next = Array.isArray(list) ? list : [];
      let totalPages = parseInt(res?.totalPages, 10);
      if (isNaN(totalPages) || totalPages < 1) {
        // Only infer when API omits/invalidates totalPages.
        totalPages = next.length >= pageSize ? pageNum + 1 : Math.max(1, pageNum);
      }
      const current = parseInt(res?.current, 10) || pageNum;
      this.setState({
        options: next,
        loading: false,
        page: Math.max(1, current),
        totalPages,
      });
    };

    if (selectedType === "rawMaterials") {
      this.props.searchItems({ page: pageNum, name: q, fromShipmentLog: true, fromInventoryTransfer: true }, (res) => {
        done(res?.items || [], res);
      });
      return;
    }

    if (selectedType === "packagingMaterials") {
      this.props.searchPackagingMaterials({ page: pageNum, name: q, fromInventoryTransfer: true }, (res) => {
        done(res?.packagingMaterials || res?.materials || [], res);
      });
      return;
    }

    const type = INVENTORY_TYPE_API[selectedType] || "Finished goods";
    this.props.searchInventoryTypes({ page: pageNum, name: q, type, fromApp: true, fromInventoryTransfer: true }, (res) => {
      done(res?.inventoryTypes || [], res);
    });
  }

  goToPage(pageNum) {
    const { loading, page, totalPages, itemSearch } = this.state;
    if (loading || pageNum < 1 || pageNum > totalPages || pageNum === page) return;
    this.fetchItems(itemSearch, pageNum);
  }

  getPageNumbers(current, total) {
    const maxButtons = 5;
    if (total <= maxButtons) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    let start = Math.max(1, current - Math.floor(maxButtons / 2));
    let end = start + maxButtons - 1;
    if (end > total) {
      end = total;
      start = Math.max(1, end - maxButtons + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  onChangeType(typeKey) {
    if (this.isCompleted()) return;
    this.setState({ selectedType: typeKey, itemSearch: "", options: [], page: 1, totalPages: 1 }, () => {
      this.fetchItems("", 1);
    });
  }

  onChangeSearch(val) {
    if (this.isCompleted()) return;
    this.setState({ itemSearch: val });
    this.scheduleFetch(val);
  }

  getItemName(row) {
    return row?.itemName || row?.item || row?.namePackaging || row?.name || "";
  }

  getItemAmount(row) {
    const lots = row?.items;
    if (!Array.isArray(lots) || !lots.length) return null;
    let total = 0;
    let hasAmount = false;
    lots.forEach((lot) => {
      const historial = Array.isArray(lot?.historial) ? lot.historial : [];
      if (historial.length) {
        historial.forEach((h) => {
          const amt = parseFloat(h?.amount != null ? h.amount : h?.editedAmount);
          if (!isNaN(amt)) {
            total += amt;
            hasAmount = true;
          }
        });
      } else {
        const amt = parseFloat(lot?.amount);
        if (!isNaN(amt)) {
          total += amt;
          hasAmount = true;
        }
      }
    });
    return hasAmount ? total : null;
  }

  getVisibleItems() {
    if (this.isCompleted()) {
      return this.getCompletedOptions().map((row) => {
        const name = this.getItemName(row);
        const amount = this.getItemAmount(row);
        return {
          item: name,
          itemName: name,
          _id: row?._id,
          type: row?.type || TYPE_API[this.state.selectedType] || "ingredient",
          inventoryType: row?.inventoryCategory || row?.inventoryType || this.state.selectedType,
          inventoryCategory: row?.inventoryCategory || row?.inventoryType || this.state.selectedType,
          productCode: row?.productCode || "",
          units: row?.units || "",
          totalAmount: amount,
          items: row?.items,
          raw: row,
        };
      });
    }
    const { options, itemSearch, selectedType } = this.state;
    const q = (itemSearch || "").trim().toLowerCase();
    let list = (options || []).map((row) => {
      const name = this.getItemName(row);
      const amount = this.getItemAmount(row);
      return {
        item: name,
        itemName: name,
        _id: row?._id,
        type: TYPE_API[selectedType] || "ingredient",
        inventoryType: selectedType,
        inventoryCategory: selectedType,
        productCode: row?.productCode || "",
        units: row?.units || "",
        totalAmount: amount,
        items: row?.items,
        raw: row,
      };
    });

    if (q) {
      list = list.filter((row) => {
        const name = String(row.item || "").toLowerCase();
        const code = String(row.productCode || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      });
      list = list.slice().sort((a, b) => sortSearch(a, b, q));
    }

    return list;
  }

  getHistorialTimestamp(h) {
    const raw = h?.production_date || h?.date || h?.createdAt || h?.created_at || h?.minDate || "";
    if (!raw) return null;
    const t = Date.parse(raw);
    return isNaN(t) ? null : t;
  }

  getFifoHistorialBatches(historial) {
    if (!Array.isArray(historial) || !historial.length) return [];
    const withDates = historial.every((h) => this.getHistorialTimestamp(h) != null);
    if (withDates) {
      return historial.slice().sort((a, b) => this.getHistorialTimestamp(a) - this.getHistorialTimestamp(b));
    }
    return historial.slice().reverse();
  }

  normalizeInitialLotcodes(raw) {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : [raw];
    return list
      .filter((l) => {
        if (l == null || l === "") return false;
        if (typeof l === "string") return String(l).trim() !== "";
        if (typeof l === "object") {
          return !!(l.lot || l.batchId || (Array.isArray(l.historial) && l.historial.length));
        }
        return false;
      })
      .map((l) => (typeof l === "string" ? { lot: l, historial: [] } : l));
  }

  getSavedEditedAmount(saved) {
    if (!saved) return null;
    if (saved.editedAmount != null && saved.editedAmount !== "") {
      const direct = parseFloat(saved.editedAmount);
      if (!isNaN(direct)) return direct;
    }
    const h = saved.historial?.[0];
    if (h) {
      const amt = parseFloat(h.editedAmount != null && h.editedAmount !== "" ? h.editedAmount : h.amount);
      if (!isNaN(amt)) return amt;
    }
    return null;
  }

  seedInitialSelection() {
    const { allowSeedInitial, selectedLots } = this.state;
    if (!allowSeedInitial || selectedLots.length) return;

    const savedLots = this.normalizeInitialLotcodes(this.props.initialLotcodes);
    const initialItem = this.getSelectedItem();
    const name =
      initialItem?.itemName ||
      initialItem?.item ||
      savedLots[0]?.item ||
      "";
    const defaultTo =
      unwrapLocationValue(this.props.toLocation) ||
      unwrapLocationValue(savedLots[0]?.toLocation) ||
      null;

    if (savedLots.length) {
      const catalog = this.props.inventoryLocations || [];
      const next = savedLots.map((saved, idx) => {
        const itemName = saved.item || name || "";
        const savedAmt = this.getSavedEditedAmount(saved);
        const savedHistorial = Array.isArray(saved.historial) ? saved.historial : [];
        const h = savedHistorial[0] || {};
        const rawLoc =
          h.location ||
          h.completeLocation ||
          unwrapLocationValue(this.props.fromLocation) ||
          null;
        const loc = resolveTransferLocation(rawLoc, catalog) || rawLoc;
        const amount =
          savedAmt != null
            ? savedAmt
            : parseFloat(h.editedAmount != null ? h.editedAmount : h.amount);
        return {
          key: saved.batchId || `saved-${itemName}-${saved.lot}-${idx}`,
          item: itemName,
          itemName,
          lot: saved.lot != null ? saved.lot : "",
          editedAmount: !isNaN(amount) ? roundTransferAmount(amount) : 0,
          units: saved.units || h.units || initialItem?.units || "",
          location: loc,
          locationLabel: getTransferLocationPathLabel(loc) || "",
          type: initialItem?.type || saved.type || "",
          inventoryType:
            initialItem?.inventoryType ||
            initialItem?.inventoryCategory ||
            saved.inventoryType ||
            "",
          _id: initialItem?._id || saved._id,
          productCode: initialItem?.productCode || saved.productCode || h.productCode || "",
          historial: savedHistorial,
          batchId: saved.batchId || null,
          toLocation: unwrapLocationValue(saved.toLocation) || defaultTo,
        };
      });
      const expandedItems = {};
      let firstItemName = null;
      next.forEach((lot) => {
        const itemName = lot.item || lot.itemName;
        if (itemName) {
          expandedItems[itemName] = false;
          if (!firstItemName) firstItemName = itemName;
        }
      });
      this.setState({
        selectedLots: next,
        expandedItems,
        selectedLotKey: null,
        selectedItemName: firstItemName,
        allowSeedInitial: false,
      });
      return;
    }

    if (initialItem && Array.isArray(initialItem.items) && initialItem.items.length) {
      this.addLotsFromItemSource(initialItem, initialItem.items, { replace: true, fromUser: false });
      this.setState({ allowSeedInitial: false });
    }
  }

  lotAlreadySelected(lot, list = this.state.selectedLots) {
    return list.some((l) => {
      if (lot.historialId && l.historialId && String(lot.historialId) === String(l.historialId)) {
        return true;
      }
      // historial batches are unique by historialId — do not collapse by lot code alone
      if (lot.historialId || l.historialId) return false;
      if (lot.batchId && l.batchId && lot.batchId === l.batchId) return true;
      return (
        (l.item || l.itemName) === (lot.item || lot.itemName) &&
        String(l.lot || "") === String(lot.lot || "") &&
        String(l.locationLabel || "") === String(lot.locationLabel || "")
      );
    });
  }

  getLotsWithStock(lots) {
    if (!Array.isArray(lots) || !lots.length) return [];
    return lots.filter((lot) => {
      const batches = Array.isArray(lot?.historial) ? lot.historial : [];
      if (!batches.length) {
        const amt = parseFloat(lot?.amount);
        return !isNaN(amt) && amt > 0;
      }
      return batches.some((h) => {
        if (h?.hide) return false;
        const amt = parseFloat(h?.amount != null ? h.amount : h?.editedAmount);
        return !isNaN(amt) && amt > 0;
      });
    });
  }

  getHistorialBatchesWithStock(lot) {
    const historial = Array.isArray(lot?.historial) ? lot.historial : [];
    if (historial.length) {
      return historial.filter((h) => {
        if (h?.hide) return false;
        const amt = parseFloat(h?.editedAmount != null ? h.editedAmount : h?.amount);
        return !isNaN(amt) && amt > 0;
      });
    }
    const amt = parseFloat(lot?.amount);
    if (!isNaN(amt) && amt > 0) {
      return [
        {
          amount: amt,
          editedAmount: amt,
          location: lot.location,
          completeLocation: lot.completeLocation,
          units: lot.units,
          lot: lot.lot,
          _id: lot._id || lot.batchId || null,
        },
      ];
    }
    return [];
  }

  addLotsFromItemSource(itemRow, lots, { replace, fromUser } = {}) {
    const itemName = itemRow?.item || itemRow?.itemName || this.getItemName(itemRow) || "";
    if (!itemName) return;
    const catalog = this.props.inventoryLocations || [];
    const fallbackFrom = unwrapLocationValue(this.props.fromLocation);
    // Include every lot code with stock — not only lots at the FIFO location.
    const usable = this.getLotsWithStock(lots);

    const defaultTo = unwrapLocationValue(this.props.toLocation) || null;
    let next = replace ? [] : [...this.state.selectedLots];
    const addedKeys = [];

    usable.forEach((lot, lotIdx) => {
      const batches = this.getHistorialBatchesWithStock(lot);
      batches.forEach((batch, batchIdx) => {
        const historialId = batch?._id || null;
        const amt = parseFloat(batch?.editedAmount != null ? batch.editedAmount : batch?.amount);
        const rawLoc = batch.location || batch.completeLocation || fallbackFrom;
        const loc = resolveTransferLocation(rawLoc, catalog) || rawLoc;
        const locationLabel =
          getTransferLocationPathLabel(loc) ||
          (typeof batch.completeLocation === "string" ? batch.completeLocation : "") ||
          "";
        const candidate = {
          historialId,
          batchId: lot.batchId || null,
          item: itemName,
          itemName,
          lot: lot.lot != null ? lot.lot : batch.lot || "",
          locationLabel,
        };
        if (this.lotAlreadySelected(candidate, next) && !replace) return;

        const key =
          historialId ||
          `${itemName}-${lot.lot || ""}-${locationLabel}-${next.length}-${lotIdx}-${batchIdx}`;
        addedKeys.push(key);
        next.push({
          key,
          item: itemName,
          itemName,
          lot: lot.lot != null ? lot.lot : batch.lot || "",
          editedAmount: roundTransferAmount(amt),
          units: batch.units || lot.units || itemRow.units || "",
          location: loc,
          locationLabel,
          type: itemRow.type || TYPE_API[this.state.selectedType] || "",
          inventoryType: itemRow.inventoryCategory || itemRow.inventoryType || this.state.selectedType,
          _id: itemRow._id,
          productCode: itemRow.productCode || batch.productCode || "",
          // One row per historial batch so each location/amount transfers separately.
          historial: [batch],
          historialId,
          batchId: historialId || lot.batchId || null,
          items: itemRow.items,
          toLocation: defaultTo,
        });
      });
    });

    if (!next.length && !replace) return;

    const expandedItems = { ...this.state.expandedItems, [itemName]: false };

    this.setState({
      selectedLots: next,
      expandedItems,
      selectedLotKey: null,
      selectedItemName: itemName,
      allowSeedInitial: false,
      ...(fromUser ? { hasUserChanges: true } : {}),
    });
  }

  onAddItem(row) {
    if (this.isReadOnly()) return;
    const apply = (lots) => {
      if (!lots?.length) {
        this.setState({ infoMessage: "No lot codes with stock found for this item." });
        return;
      }
      this.setState({ infoMessage: "" });
      this.addLotsFromItemSource(row, lots, { replace: false, fromUser: true });
    };

    if (Array.isArray(row.items) && row.items.length) {
      apply(row.items);
      return;
    }

    const type = row.type || TYPE_API[this.state.selectedType] || "ingredient";
    const name = row.item || row.itemName;
    this.setState({ loading: true, infoMessage: "" });
    this.props.getIngredientLotsByName({ name, type }, (res) => {
      this.setState({ loading: false });
      apply(res?.lots || []);
    });
  }

  selectLotRow(lotKey) {
    this.setState({
      selectedLotKey: lotKey,
      selectedItemName: null,
      showToLocationDropdown: false,
      toLocationSearch: "",
    });
  }

  selectItem(itemName) {
    if (!itemName) return;
    this.setState({
      selectedItemName: itemName,
      selectedLotKey: null,
      showToLocationDropdown: false,
      toLocationSearch: "",
    });
  }

  selectToLocation(location, lotKey, itemName) {
    if (this.isReadOnly()) return false;
    const bulkItemName = itemName || (!lotKey ? this.state.selectedItemName : null);
    const key = lotKey || this.state.selectedLotKey;
    const lotsToCheck = bulkItemName
      ? this.state.selectedLots.filter((l) => (l.item || l.itemName) === bulkItemName)
      : this.state.selectedLots.filter((l) => l.key === key);
    const sameAsCurrent = lotsToCheck.some((l) => locationsRoughlyMatch(l.location, location));
    if (sameAsCurrent) {
      this.setState({
        showToLocationDropdown: true,
        toLocationError: "Destination location must be different from the current location.",
      });
      return false;
    }
    let selectedLots;
    if (bulkItemName) {
      selectedLots = this.state.selectedLots.map((l) =>
        (l.item || l.itemName) === bulkItemName ? { ...l, toLocation: location } : l
      );
    } else {
      if (!key) return false;
      selectedLots = this.state.selectedLots.map((l) =>
        l.key === key ? { ...l, toLocation: location } : l
      );
    }
    this.setState({
      selectedLots,
      showToLocationDropdown: false,
      toLocationSearch: "",
      hasUserChanges: true,
      infoMessage: "",
      toLocationError: "",
    });
    return true;
  }

  removeLot(key) {
    if (this.isReadOnly()) return;
    const removed = this.state.selectedLots.find((l) => l.key === key);
    const selectedLots = this.state.selectedLots.filter((l) => l.key !== key);
    const updates = { selectedLots, hasUserChanges: true };
    if (this.state.selectedLotKey === key) {
      updates.selectedLotKey = null;
      updates.showToLocationDropdown = false;
    }
    const removedItem = removed?.item || removed?.itemName;
    if (removedItem && this.state.selectedItemName === removedItem) {
      const stillHasItem = selectedLots.some((l) => (l.item || l.itemName) === removedItem);
      if (!stillHasItem) {
        updates.selectedItemName = selectedLots[0]
          ? selectedLots[0].item || selectedLots[0].itemName || null
          : null;
      }
    }
    if (!selectedLots.length) {
      updates.selectedItemName = null;
      updates.selectedLotKey = null;
    }
    this.setState(updates);
  }

  editLotAmount(key, value) {
    if (this.isReadOnly()) return;
    const selectedLots = this.state.selectedLots.map((l) =>
      l.key === key ? { ...l, editedAmount: value } : l
    );
    this.setState({ selectedLots, hasUserChanges: true });
  }

  toggleItemExpanded(itemName) {
    const expandedItems = { ...this.state.expandedItems };
    const nextExpanded = expandedItems[itemName] !== true;
    expandedItems[itemName] = nextExpanded;
    const updates = { expandedItems };
    if (!nextExpanded) {
      updates.selectedItemName = itemName;
      updates.selectedLotKey = null;
      updates.showToLocationDropdown = false;
      updates.toLocationSearch = "";
    }
    this.setState(updates);
  }

  onSave() {
    const { onSave, onClose } = this.props;
    const { selectedLots } = this.state;
    const activeLots = selectedLots.filter((l) => !l.isSeed || l.lot);
    if (!activeLots.length) {
      this.setState({ infoMessage: "Please select at least one item to transfer." });
      return;
    }
    const missingTo = findLotMissingToLocation(activeLots, this.props.toLocation);
    if (missingTo) {
      this.selectLotRow(missingTo.key);
      this.setState({
        infoMessage: `Please select a Transfer to location for lot "${missingTo.lot || "No Lot"}" (${
          missingTo.item || missingTo.itemName || "item"
        }).`,
      });
      return;
    }
    const rows = buildInventoryTransferSaveRows(
      activeLots,
      null,
      this.props.inventoryLocations || []
    );
    if (!rows.length) {
      this.setState({ infoMessage: "Please select at least one item to transfer." });
      return;
    }
    this.setState({ infoMessage: "" });
    if (onSave) onSave(rows);
    if (onClose) onClose();
  }

  formatAmount(row) {
    const amt = row?.totalAmount;
    const units = row?.units || "";
    if (amt == null || isNaN(amt)) return units || "—";
    const shown =
      amt % 1 !== 0 && !validateDecimals(String(amt))
        ? Number(amt.toFixed(3))
        : amt;
    return units ? `${shown} ${units}` : String(shown);
  }

  renderSelectedSection() {
    if (!this.state.selectedLots?.length) return null;
    return (
      <InventoryTransferLotsPanel
        selectedLots={this.state.selectedLots}
        selectedLotKey={this.state.selectedLotKey}
        selectedItemName={this.state.selectedItemName}
        expandedItems={this.state.expandedItems}
        showToLocationDropdown={this.state.showToLocationDropdown}
        toLocationSearch={this.state.toLocationSearch}
        inventoryLocations={this.props.inventoryLocations || []}
        readOnly={this.isReadOnly()}
        fallbackToLocation={this.props.toLocation}
        showAllLotCodesInfo
        toLocationError={this.state.toLocationError}
        onDismissToLocationError={() =>
          this.setState({ toLocationError: "", showToLocationDropdown: true })
        }
        onSelectLotKey={(key) => this.selectLotRow(key)}
        onSelectItem={(name) => this.selectItem(name)}
        onRemoveLot={(key) => this.removeLot(key)}
        onEditLotAmount={(key, val) => this.editLotAmount(key, val)}
        onToggleItemExpanded={(name) => this.toggleItemExpanded(name)}
        onToggleToLocationDropdown={(open) =>
          this.setState({
            showToLocationDropdown: !!open,
            toLocationSearch: open ? this.state.toLocationSearch : "",
          })
        }
        onChangeToLocationSearch={(val) => this.setState({ toLocationSearch: val })}
        onSelectToLocation={(loc, lotKey, itemName) =>
          this.selectToLocation(loc, lotKey, itemName)
        }
      />
    );
  }

  renderPagination() {
    const { page, totalPages, loading } = this.state;
    if (this.isCompleted() || totalPages <= 1) return null;
    const pages = this.getPageNumbers(page, totalPages);
    return (
      <View style={styles.pagination}>
        <TouchableOpacity
          style={[styles.pageNavBtn, (page <= 1 || loading) && styles.pageNavBtnDisabled]}
          disabled={page <= 1 || loading}
          onPress={() => this.goToPage(page - 1)}
        >
          <MaterialIcons name="chevron-left" size={24} color={page <= 1 || loading ? "#aaa" : colors.primary} />
        </TouchableOpacity>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.paginationPages}>
          {pages.map((p) => {
            const active = p === page;
            return (
              <TouchableOpacity
                key={`page-${p}`}
                style={[styles.pageBtn, active && styles.pageBtnActive]}
                disabled={loading || active}
                onPress={() => this.goToPage(p)}
              >
                <Text style={[styles.pageBtnTxt, active && styles.pageBtnTxtActive]}>{p}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
        <TouchableOpacity
          style={[styles.pageNavBtn, (page >= totalPages || loading) && styles.pageNavBtnDisabled]}
          disabled={page >= totalPages || loading}
          onPress={() => this.goToPage(page + 1)}
        >
          <MaterialIcons
            name="chevron-right"
            size={24}
            color={page >= totalPages || loading ? "#aaa" : colors.primary}
          />
        </TouchableOpacity>
      </View>
    );
  }

  render() {
    const { onClose } = this.props;
    const { itemSearch, selectedType, loading, hasUserChanges } = this.state;
    const items = this.getVisibleItems();
    const completed = this.isCompleted();
    const readOnly = this.isReadOnly();

    return (
      <Modal animationType="slide" transparent={false} visible={true} onRequestClose={onClose}>
        <View style={styles.screen}>
          <View style={styles.card}>
            <View style={styles.header}>
              <TouchableOpacity style={styles.backBtn} onPress={onClose}>
                <MaterialIcons name="arrow-back" size={26} color={colors.primary} />
              </TouchableOpacity>
              <Text style={styles.headerTitle}>Items</Text>
              <View style={styles.backBtn} />
            </View>

            <ScrollView style={styles.bodyScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
              {this.renderSelectedSection()}

              {!completed ? (
                <View style={styles.searchBar}>
                  <MaterialIcons name="search" size={20} color={colors.orange} style={styles.searchIcon} />
                  <TextInput
                    style={styles.searchInput}
                    value={itemSearch}
                    placeholder="Search items"
                    placeholderTextColor="#939598"
                    onChangeText={(val) => this.onChangeSearch(val)}
                  />
                  <MaterialIcons name="filter-list" size={20} color={colors.orange} />
                </View>
              ) : null}

              {!completed ? (
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabsScroll}>
                  {TYPE_TABS.map((tab) => {
                    const active = selectedType === tab.key;
                    return (
                      <TouchableOpacity
                        key={tab.key}
                        style={[styles.typeTab, active && styles.typeTabActive]}
                        onPress={() => this.onChangeType(tab.key)}
                      >
                        <Text style={[styles.typeTabTxt, active && styles.typeTabTxtActive]}>
                          {tab.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              ) : null}

              {!completed ? (
                <View>
                  <View style={styles.tableHeader}>
                    <Text style={[styles.th, styles.colAdd]} />
                    <Text style={[styles.th, styles.colName]}>Name</Text>
                    <Text style={[styles.th, styles.colAmount]}>Amount</Text>
                  </View>

                  {loading ? (
                    <View style={styles.loadingBox}>
                      <ActivityIndicator size="large" color={colors.orange} />
                    </View>
                  ) : (
                    <View>
                      {!items.length ? (
                        <Text style={styles.emptyTxt}>No items found</Text>
                      ) : (
                        items.map((row, idx) => (
                          <TouchableOpacity
                            key={`${row._id || row.item}-${idx}`}
                            style={styles.tableRow}
                            onPress={() => this.onAddItem(row)}
                          >
                            <View style={[styles.td, styles.colAdd]}>
                              <MaterialCommunityIcons name="plus-circle" size={26} color={colors.add} />
                            </View>
                            <Text style={[styles.td, styles.colName]} numberOfLines={2}>
                              {row.item}
                            </Text>
                            <Text style={[styles.td, styles.colAmount]} numberOfLines={1}>
                              {this.formatAmount(row)}
                            </Text>
                          </TouchableOpacity>
                        ))
                      )}
                    </View>
                  )}
                </View>
              ) : null}
            </ScrollView>

            {this.renderPagination()}

            {this.state.infoMessage ? (
              <View style={styles.infoBanner}>
                <MaterialIcons name="info-outline" size={18} color={colors.primary} style={styles.infoIcon} />
                <Text style={styles.infoTxt}>{this.state.infoMessage}</Text>
              </View>
            ) : null}

            {readOnly ? (
              <View style={styles.footer}>
                <TouchableOpacity style={styles.saveBtn} onPress={onClose}>
                  <Text style={styles.saveTxt}>Close</Text>
                </TouchableOpacity>
              </View>
            ) : hasUserChanges ? (
              <View style={styles.footer}>
                <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
                  <Text style={styles.cancelTxt}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.saveBtn} onPress={() => this.onSave()}>
                  <Text style={styles.saveTxt}>Save</Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
        </View>
      </Modal>
    );
  }
}

export default connect(
  null,
  (dispatch) => ({
    searchItems: (data, callback) => dispatch(searchItems(data, callback)),
    searchInventoryTypes: (data, callback) => dispatch(searchInventoryTypes(data, callback)),
    searchPackagingMaterials: (data, callback) => dispatch(searchPackagingMaterials(data, callback)),
    getIngredientLotsByName: (data, callback) => dispatch(getIngredientLotsByName(data, callback)),
  })
)(InventoryTransferItemModal);

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.gray,
    paddingTop: Platform.OS === "ios" ? 40 : 10,
  },
  card: {
    flex: 1,
    backgroundColor: "#fff",
    margin: 10,
    borderRadius: 12,
    padding: 12,
    marginInline: isMobileWeb() ? "1" : "20%",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  backBtn: { width: 36, height: 36, alignItems: "center", justifyContent: "center" },
  headerTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: colors.primary,
    fontFamily: "Roboto",
  },
  bodyScroll: { flex: 1 },
  searchBar: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 10,
    marginTop: 10,
  },
  searchIcon: { marginRight: 6 },
  searchInput: {
    flex: 1,
    fontSize: 15,
    color: "#333",
    padding: 0,
    fontFamily: "Roboto",
  },
  tabsScroll: {
    maxHeight: 44,
    marginBottom: 10,
  },
  typeTab: {
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: 18,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
  },
  typeTabActive: {
    backgroundColor: colors.primary,
  },
  typeTabTxt: {
    color: colors.primary,
    fontSize: 13,
    fontWeight: "600",
    fontFamily: "Roboto",
  },
  typeTabTxtActive: {
    color: "#fff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  th: { fontSize: 13, fontWeight: "700", color: "#333", fontFamily: "Roboto" },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingVertical: 10,
    paddingHorizontal: 4,
  },
  td: { fontSize: 13, color: "#333", fontFamily: "Roboto" },
  colAdd: { width: "12%", alignItems: "center" },
  colName: { width: "58%" },
  colAmount: { width: "30%" },
  loadingBox: { padding: 24, alignItems: "center" },
  emptyTxt: {
    textAlign: "center",
    color: "#888",
    padding: 16,
    fontFamily: "Roboto",
  },
  pagination: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingBottom: 4,
    borderTopWidth: 1,
    borderTopColor: "#eee",
    marginTop: 4,
  },
  paginationPages: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 4,
  },
  pageNavBtn: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
  },
  pageNavBtnDisabled: {
    opacity: 0.5,
  },
  pageBtn: {
    minWidth: 32,
    height: 32,
    marginHorizontal: 3,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: colors.primary,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  pageBtnActive: {
    backgroundColor: colors.primary,
  },
  pageBtnTxt: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Roboto",
  },
  pageBtnTxtActive: {
    color: "#fff",
  },
  infoBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: colors.primary_light,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginTop: 8,
  },
  infoIcon: { marginRight: 8, marginTop: 1 },
  infoTxt: {
    flex: 1,
    fontSize: 13,
    color: colors.primary,
    fontFamily: "Roboto",
    lineHeight: 18,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 12,
  },
  cancelBtn: {
    flex: 1,
    borderWidth: 1.5,
    borderColor: colors.quit,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: "center",
    marginRight: 6,
  },
  cancelTxt: {
    color: colors.quit,
    fontSize: 16,
    fontWeight: "700",
    fontFamily: "Roboto",
  },
  saveBtn: {
    flex: 1,
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: "center",
    marginLeft: 6,
  },
  saveTxt: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
    fontFamily: "Roboto",
  },
});
